import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { CHALLENGE_DAYS } from "@/lib/challengeData";
import { CheckCircle2 } from "lucide-react";

export default function Challenge() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["challenge"],
    queryFn: async () => (await http.get("/challenge")).data,
  });
  const completed = new Set(data?.completed_days || []);
  const doneCount = completed.size;
  const pct = Math.round((doneCount / 30) * 100);

  const toggle = useMutation({
    mutationFn: async ({ day, completed }) => http.post("/challenge/toggle", { day, completed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["challenge"] }),
  });

  return (
    <div className="space-y-5" data-testid="challenge-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">30-Day Patient Flow Challenge</h1>
        <p className="text-sm text-zinc-500 mt-1">One practical acquisition action every day. Build momentum in a month.</p>
      </div>

      <Card className="p-5 border-emerald-200 bg-emerald-50/50">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Your progress</div>
            <div className="mt-1 font-heading text-2xl font-semibold text-zinc-900">Day {doneCount} of 30 completed</div>
          </div>
          <div className="text-4xl font-heading font-semibold text-emerald-700">{pct}%</div>
        </div>
        <Progress value={pct} className="mt-4 h-2" data-testid="challenge-progress" />
      </Card>

      <div className="grid gap-3">
        {CHALLENGE_DAYS.map((d) => {
          const done = completed.has(d.day);
          return (
            <Card key={d.day} className={`p-4 border transition-colors duration-200 ${done ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200"}`} data-testid={`challenge-day-${d.day}`}>
              <div className="flex gap-4 items-start">
                <div className="shrink-0">
                  <Checkbox
                    checked={done}
                    onCheckedChange={(v) => toggle.mutate({ day: d.day, completed: !!v })}
                    className="mt-1"
                    data-testid={`day-check-${d.day}`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Day {d.day}</span>
                    {done && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                  </div>
                  <div className={`mt-1 font-heading text-base font-medium ${done ? "text-zinc-500 line-through" : "text-zinc-900"}`}>{d.action}</div>
                  <div className="text-sm text-zinc-500 mt-1 leading-relaxed">{d.explain}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
